import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import { useAuthStore } from './store/authStore';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Layout from './components/Layout';
import Students from './pages/Students';
import Staff from './pages/Staff';
import Courses from './pages/Courses';
import Subjects from './pages/Subjects';
import Expenses from './pages/Expenses';
import RecurringExpenses from './pages/RecurringExpenses';
import Income from './pages/Income';
import Salary from './pages/Salary';
import Bonus from './pages/Bonus';
import Certificates from './pages/Certificates';
import CertificateTemplates from './pages/CertificateTemplates';
import Invoices from './pages/Invoices';
import PaymentNotifications from './pages/PaymentNotifications';
import Reports from './pages/Reports';
import CourseReport from './pages/CourseReport';
import StaffReport from './pages/StaffReport';
import Academies from './pages/Academies';
import Users from './pages/Users';
import StudentDetails from './pages/StudentDetails';
import CourseDetails from './pages/CourseDetails';
import StaffDetails from './pages/StaffDetails';
import SocialMessaging from './pages/SocialMessaging';
import SocialIntegrations from './pages/SocialIntegrations';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import AuditLogs from './pages/AuditLogs';
import OnlineStudents from './pages/OnlineStudents';
import OnlineStudentDetail from './pages/OnlineStudentDetail';
import OnlineExpenses from './pages/OnlineExpenses';
import OnlineIncome from './pages/OnlineIncome';
import OnlineReports from './pages/OnlineReports';

const PrivateRoute = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const token = useAuthStore((state) => state.token);

    // Double check with localStorage for mobile compatibility
    const storageToken = localStorage.getItem('token');

    // Consider authenticated if either Zustand or localStorage has the token
    const isAuth = isAuthenticated || (token && storageToken);

    return isAuth ? children : <Navigate to="/login" replace />;
};

const SuperAdminRoute = ({ children }) => {
    const user = useAuthStore((state) => state.user);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (!isAuthenticated) return <Navigate to="/login" />;
    if (user?.role !== 'superadmin') return <Navigate to="/dashboard" />;

    return children;
};

const AdminRoute = ({ children }) => {
    const user = useAuthStore((state) => state.user);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (!isAuthenticated) return <Navigate to="/login" />;
    if (user?.role !== 'admin') return <Navigate to="/dashboard" />;

    return children;
};

const DashboardRoute = ({ children }) => {
    const user = useAuthStore((state) => state.user);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (!isAuthenticated) return <Navigate to="/login" />;
    if (user?.role === 'dataentry') return <Navigate to="/students" />;

    return children;
};

function App() {
    const { i18n } = useTranslation();
    const user = useAuthStore((state) => state.user);
    const [direction, setDirection] = React.useState('rtl');
    const [locale, setLocale] = React.useState(arEG);

    // Set language from user preferences on mount
    useEffect(() => {
        if (user?.language) {
            i18n.changeLanguage(user.language);
        }
    }, [user, i18n]);

    useEffect(() => {
        const updateDirection = () => {
            const dir = i18n.language === 'en' ? 'ltr' : 'rtl';
            setDirection(dir);
            document.documentElement.dir = dir;
            document.documentElement.lang = i18n.language;

            // Update locale
            if (i18n.language === 'en') {
                setLocale(enUS);
            } else {
                setLocale(arEG); // Use Arabic locale for Kurdish and Arabic
            }
        };

        updateDirection();
        i18n.on('languageChanged', updateDirection);

        return () => {
            i18n.off('languageChanged', updateDirection);
        };
    }, [i18n]);

    return (
        <ConfigProvider
            direction={direction}
            locale={locale}
            theme={{
                token: {
                    colorPrimary: '#2563eb',
                    colorSuccess: '#10b981',
                    colorWarning: '#f59e0b',
                    colorError: '#ef4444',
                    colorInfo: '#14b8a6',
                    borderRadius: 10,
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
                },
                components: {
                    Button: {
                        controlHeight: 40,
                        controlHeightLG: 48,
                        fontWeight: 500,
                    },
                    Input: {
                        controlHeight: 40,
                        controlHeightLG: 48,
                    },
                    Select: {
                        controlHeight: 40,
                        controlHeightLG: 48,
                    },
                    Card: {
                        borderRadiusLG: 16,
                    },
                    Table: {
                        borderRadiusLG: 12,
                    },
                },
            }}
        >
            <Routes>
                <Route path="/login" element={<Login />} />

                <Route
                    path="/"
                    element={
                        <PrivateRoute>
                            <Layout />
                        </PrivateRoute>
                    }
                >
                    <Route index element={<Navigate to="/dashboard" />} />
                    <Route path="dashboard" element={
                        <DashboardRoute>
                            <Dashboard />
                        </DashboardRoute>
                    } />
                    <Route path="profile" element={<Profile />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="students" element={<Students />} />
                    <Route path="students/:id" element={<StudentDetails />} />
                    <Route path="staff" element={<Staff />} />
                    <Route path="staff/:id" element={<StaffDetails />} />
                    <Route path="courses" element={<Courses />} />
                    <Route path="courses/:id" element={<CourseDetails />} />
                    <Route path="subjects" element={<Subjects />} />
                    <Route path="expenses" element={<Expenses />} />
                    <Route path="recurring-expenses" element={<RecurringExpenses />} />
                    <Route path="income" element={<Income />} />
                    <Route path="salary" element={<Salary />} />
                    <Route path="bonus" element={<Bonus />} />
                    <Route path="certificates" element={<Certificates />} />
                    <Route path="invoices" element={<Invoices />} />
                    <Route path="payment-notifications" element={<PaymentNotifications />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="course-report" element={<CourseReport />} />
                    <Route path="staff-report" element={<StaffReport />} />
                    <Route path="users" element={<Users />} />
                    <Route path="social-messaging" element={<SocialMessaging />} />
                    <Route
                        path="social-integrations"
                        element={
                            <AdminRoute>
                                <SocialIntegrations />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="certificate-templates"
                        element={
                            <AdminRoute>
                                <CertificateTemplates />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="audit-logs"
                        element={
                            <AdminRoute>
                                <AuditLogs />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="online-students"
                        element={
                            <AdminRoute>
                                <OnlineStudents />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="online-students/:id"
                        element={
                            <AdminRoute>
                                <OnlineStudentDetail />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="online-expenses"
                        element={
                            <AdminRoute>
                                <OnlineExpenses />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="online-income"
                        element={
                            <AdminRoute>
                                <OnlineIncome />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="online-reports"
                        element={
                            <AdminRoute>
                                <OnlineReports />
                            </AdminRoute>
                        }
                    />

                    <Route
                        path="academies"
                        element={
                            <SuperAdminRoute>
                                <Academies />
                            </SuperAdminRoute>
                        }
                    />
                </Route>

                <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
        </ConfigProvider>
    );
}

export default App;
